/* saif-ab.js — the smallest A/B framework that answers "which one sells more".
 *
 * Design notes, because the details are what make or break a test:
 *
 *  · ASSIGNMENT IS STICKY PER DEVICE. A visitor who is bounced to the free lesson on
 *    Monday must not be bounced to the course page on Tuesday, or neither number means
 *    anything. The draw happens once and lives in localStorage.
 *
 *  · THE VARIANT IS A PERSON PROPERTY, not just an event property. That is the whole
 *    point: it lets every LATER event — product_viewed, purchase_completed, revenue —
 *    be split by arm in PostHog without any join. An event property would only tell us
 *    about the moment of assignment, which is the least interesting part.
 *
 *  · ASSIGNMENT IS LOGGED SEPARATELY FROM EXPOSURE. Someone can be assigned and never
 *    reach the screen. Comparing conversion across everyone assigned, rather than
 *    everyone exposed, quietly dilutes both arms.
 *
 *  · ?ab=<variant> FORCES AN ARM for testing, and is recorded as forced so QA traffic
 *    can be excluded later.
 *
 * Usage:
 *    SaifAB.assign('quiz_destination', ['lesson', 'course'])  -> 'lesson' | 'course'
 *    SaifAB.expose('quiz_destination')                        -> logs that they saw it
 */
(function () {
  var KEY = 'saif_ab_v1';

  function readAll() {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
  }
  function writeAll(o) {
    try { localStorage.setItem(KEY, JSON.stringify(o)); } catch (e) { /* private mode */ }
  }
  function track(event, props) {
    if (window.SaifTrack) window.SaifTrack(event, props);
    else if (window.posthog && posthog.capture) posthog.capture(event, props);
  }

  var SaifAB = {
    /* Returns this visitor's arm for `name`, drawing once and remembering it. */
    assign: function (name, arms) {
      var all = readAll();
      var forced = null;
      try {
        var q = new URLSearchParams(location.search).get('ab');
        if (q && arms.indexOf(q) !== -1) forced = q;
      } catch (e) { /* no URLSearchParams */ }

      if (forced) {
        all[name] = { v: forced, forced: true, at: new Date().toISOString() };
        writeAll(all);
        this._identify(name, forced, true);
        return forced;
      }

      if (all[name] && arms.indexOf(all[name].v) !== -1) return all[name].v;

      var v = arms[Math.floor(Math.random() * arms.length)];
      all[name] = { v: v, forced: false, at: new Date().toISOString() };
      writeAll(all);

      /* First draw only — this is the event that defines the cohort. */
      track('ab_assigned', { experiment: name, variant: v });
      this._identify(name, v, false);
      return v;
    },

    /* Two mechanisms, on purpose — verified 2026-08-02 against the real project:
     *
     *  1. PERSON PROPERTY. Segments every later event by arm with no join. The project
     *     runs person_profiles:'identified_only', so anonymous visitors normally get no
     *     profile at all — but calling setPersonProperties explicitly forces one. Tested
     *     from a logged-out visitor: the profile was created and carried the property.
     *
     *  2. SUPER PROPERTY (register). Stamps the variant onto every SUBSEQUENT event from
     *     this device, stored browser-side. This is the belt to the person property's
     *     braces: it survives even if profile creation or identity merging misbehaves,
     *     and it means each event carries its own arm rather than relying on a lookup.
     *     Tested: the next event after register() carried it.
     */
    _identify: function (name, variant, forced) {
      var props = {};
      props['ab_' + name] = variant;
      if (forced) props['ab_' + name + '_forced'] = true;
      try {
        if (window.posthog && posthog.setPersonProperties) posthog.setPersonProperties(props);
        else if (window.posthog && posthog.capture) posthog.capture('$set', { $set: props });
      } catch (e) { /* analytics blocked — the test still runs, we just cannot read it */ }
      try {
        if (window.posthog && posthog.register) posthog.register(props);
      } catch (e) { /* same */ }
    },

    /* Re-stamp on every page load, not just the draw. register() lives in browser
       storage, but a visitor assigned before this shipped — or whose storage was
       cleared while localStorage survived — would otherwise send unstamped events. */
    restamp: function (name) {
      var v = this.get(name);
      if (v) this._identify(name, v, false);
      return v;
    },

    /* Log that the visitor actually reached the thing being tested. Fires once per
       page view; conversion should be measured against this, not against assign(). */
    expose: function (name, extra) {
      var v = this.get(name);
      if (!v) return null;
      var props = { experiment: name, variant: v };
      for (var k in (extra || {})) props[k] = extra[k];
      track('ab_exposed', props);
      return v;
    },

    get: function (name) {
      var a = readAll()[name];
      return a ? a.v : null;
    },

    /* Escape hatch for QA. */
    reset: function () { writeAll({}); },
  };

  window.SaifAB = SaifAB;
})();
